import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  addDoc,
  getDocs,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../lib/firebase/config';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ICChannel {
  id: string;
  name: string;
  createdBy: string;
  createdAt: Timestamp | null;
}

export type ICMessageType = 'channel' | 'direct';

export interface ICMessage {
  id: string;
  messageType: ICMessageType;
  senderId: string;
  senderName: string;
  senderEmail: string;
  channelId?: string;
  recipientId?: string;
  recipientName?: string;
  audioUrl: string;
  storagePath: string;
  duration: number;
  timestamp: Timestamp | null;
  expiresAt: Timestamp | null;
}

// ── Firestore paths ──────────────────────────────────────────────────────────

const CHANNELS_COLLECTION = 'instantConnectChannels';
const MESSAGES_COLLECTION = 'instantConnectMessages';
const STORAGE_FOLDER = 'instant-connect';

// ── Channel management ───────────────────────────────────────────────────────

export function subscribeToChannels(
  onChange: (channels: ICChannel[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, CHANNELS_COLLECTION), orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    (snap) => {
      const channels = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<ICChannel, 'id'>),
      }));
      onChange(channels);
    },
    (err) => {
      console.error('[InstantConnect] subscribeToChannels error:', err);
      onError?.(err as Error);
    },
  );
}

export async function createChannel(name: string, createdBy: string): Promise<string> {
  const ref = doc(collection(db, CHANNELS_COLLECTION));
  await setDoc(ref, {
    name,
    createdBy,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function deleteChannel(channelId: string): Promise<void> {
  await deleteDoc(doc(db, CHANNELS_COLLECTION, channelId));
}

// ── Audio upload ─────────────────────────────────────────────────────────────

function audioExtFromBlob(blob: Blob): { ext: string; contentType: string } {
  const type = blob.type || 'audio/webm';
  if (type.includes('mp4') || type.includes('m4a')) return { ext: 'm4a', contentType: type };
  if (type.includes('ogg')) return { ext: 'ogg', contentType: type };
  if (type.includes('wav')) return { ext: 'wav', contentType: type };
  return { ext: 'webm', contentType: type };
}

export async function uploadVoiceClip(blob: Blob, messageId: string): Promise<{ audioUrl: string; storagePath: string }> {
  const { ext, contentType } = audioExtFromBlob(blob);
  const path = `${STORAGE_FOLDER}/${messageId}.${ext}`;
  const sRef = storageRef(storage, path);
  await uploadBytes(sRef, blob, { contentType });
  const audioUrl = await getDownloadURL(sRef);
  return { audioUrl, storagePath: path };
}

// ── Send message ─────────────────────────────────────────────────────────────

interface SendMessageParams {
  senderId: string;
  senderName: string;
  senderEmail: string;
  audioBlob: Blob;
  duration: number;
  channelId?: string;
  recipientId?: string;
  recipientName?: string;
}

export async function sendVoiceMessage(params: SendMessageParams): Promise<string> {
  const { senderId, senderName, senderEmail, audioBlob, duration, channelId, recipientId, recipientName } = params;

  const isDirect = !!recipientId;
  const isChannel = !!channelId;
  if (isDirect === isChannel) {
    throw new Error('Message must target exactly one channel or one recipient');
  }

  const ref = doc(collection(db, MESSAGES_COLLECTION));
  const { audioUrl, storagePath } = await uploadVoiceClip(audioBlob, ref.id);

  const now = Timestamp.now();
  const expiresAt = Timestamp.fromMillis(now.toMillis() + 24 * 60 * 60 * 1000);

  const messageData: Omit<ICMessage, 'id'> = isDirect
    ? {
        messageType: 'direct',
        senderId,
        senderName,
        senderEmail,
        recipientId: recipientId!,
        recipientName: recipientName || '',
        audioUrl,
        storagePath,
        duration,
        timestamp: serverTimestamp() as unknown as Timestamp,
        expiresAt,
      }
    : {
        messageType: 'channel',
        senderId,
        senderName,
        senderEmail,
        channelId: channelId!,
        audioUrl,
        storagePath,
        duration,
        timestamp: serverTimestamp() as unknown as Timestamp,
        expiresAt,
      };

  await setDoc(ref, messageData);
  return ref.id;
}

// ── Listen for messages ──────────────────────────────────────────────────────

export function subscribeToChannelMessages(
  channelId: string,
  onChange: (messages: ICMessage[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, MESSAGES_COLLECTION),
    where('channelId', '==', channelId),
    orderBy('timestamp', 'desc'),
    limit(20),
  );
  return onSnapshot(
    q,
    (snap) => {
      const msgs = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ICMessage, 'id'>) }));
      onChange(msgs);
    },
    (err) => {
      console.error('[InstantConnect] subscribeToChannelMessages error:', err);
      onError?.(err as Error);
    },
  );
}

/** Messages between two users (both directions). */
export function subscribeToDirectConversation(
  myUserId: string,
  otherUserId: string,
  onChange: (messages: ICMessage[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  let outbound: ICMessage[] = [];
  let inbound: ICMessage[] = [];

  const emit = () => {
    const merged = [...outbound, ...inbound]
      .sort((a, b) => (b.timestamp?.toMillis() ?? 0) - (a.timestamp?.toMillis() ?? 0))
      .slice(0, 20);
    onChange(merged);
  };

  const outboundQ = query(
    collection(db, MESSAGES_COLLECTION),
    where('messageType', '==', 'direct'),
    where('senderId', '==', myUserId),
    where('recipientId', '==', otherUserId),
    orderBy('timestamp', 'desc'),
    limit(20),
  );
  const inboundQ = query(
    collection(db, MESSAGES_COLLECTION),
    where('messageType', '==', 'direct'),
    where('senderId', '==', otherUserId),
    where('recipientId', '==', myUserId),
    orderBy('timestamp', 'desc'),
    limit(20),
  );

  const unsubOut = onSnapshot(
    outboundQ,
    (snap) => {
      outbound = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ICMessage, 'id'>) }));
      emit();
    },
    (err) => {
      console.error('[InstantConnect] subscribeToDirectConversation outbound error:', err);
      onError?.(err as Error);
    },
  );
  const unsubIn = onSnapshot(
    inboundQ,
    (snap) => {
      inbound = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ICMessage, 'id'>) }));
      emit();
    },
    (err) => {
      console.error('[InstantConnect] subscribeToDirectConversation inbound error:', err);
      onError?.(err as Error);
    },
  );

  return () => {
    unsubOut();
    unsubIn();
  };
}

/** Subscribe to ALL new messages (for listener auto-play). Filters by recency. */
export function subscribeToAllMessages(
  onChange: (messages: ICMessage[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const fiveMinAgo = Timestamp.fromMillis(Date.now() - 5 * 60 * 1000);
  const q = query(
    collection(db, MESSAGES_COLLECTION),
    where('timestamp', '>=', fiveMinAgo),
    orderBy('timestamp', 'desc'),
    limit(10),
  );
  return onSnapshot(
    q,
    (snap) => {
      const msgs = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ICMessage, 'id'>) }));
      onChange(msgs);
    },
    (err) => {
      console.error('[InstantConnect] subscribeToAllMessages error:', err);
      onError?.(err as Error);
    },
  );
}

// ── Fetch IC-enabled users ───────────────────────────────────────────────────

export interface ICUser {
  uid: string;
  displayName: string;
  email: string;
  photoURL: string | null;
}

export async function fetchICUsers(): Promise<ICUser[]> {
  const q = query(collection(db, 'users'), where('instantConnectAccess', '==', true));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      uid: (data.uid as string) || d.id,
      displayName: data.displayName || '',
      email: data.email || '',
      photoURL: data.photoURL || null,
    };
  });
}

/** Whether this message should auto-play for the given user. */
export function shouldDeliverMessage(msg: ICMessage, myUserId: string): boolean {
  if (msg.senderId === myUserId) return false;

  const type = msg.messageType ?? (msg.channelId ? 'channel' : msg.recipientId ? 'direct' : undefined);
  if (type === 'direct') {
    return msg.recipientId === myUserId;
  }
  if (type === 'channel') {
    return !!msg.channelId;
  }
  return false;
}

// ── Cleanup expired messages ─────────────────────────────────────────────────

export async function cleanupExpiredMessages(): Promise<number> {
  const now = Timestamp.now();
  const q = query(
    collection(db, MESSAGES_COLLECTION),
    where('expiresAt', '<=', now),
  );
  const snap = await getDocs(q);
  let deleted = 0;
  for (const d of snap.docs) {
    const data = d.data();
    try {
      if (data.storagePath) {
        await deleteObject(storageRef(storage, data.storagePath));
      }
    } catch {
      // Storage file may already be gone
    }
    await deleteDoc(d.ref);
    deleted++;
  }
  return deleted;
}
